<template>
  <div>
    <div class="traffic-chart-toolbar"><a-segmented v-model:value="granularity" :options="[{label:'按小时',value:'hour'},{label:'按天',value:'day'}]" @change="loadAndRender" /></div>
    <div ref="chartRef" style="width:100%;height:320px"></div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, onUnmounted, nextTick } from 'vue';
import * as echarts from 'echarts';
import { getProxyTraffic } from '../api/proxies';
import type { TrafficBucket } from '../types';
import { formatTrafficBytes } from '../utils/trafficFormat';

const props = defineProps<{ proxyId: number }>();

const formatBytes = formatTrafficBytes;
const granularity = ref<'hour' | 'day'>('hour');

const chartRef = ref<HTMLElement | null>(null);
let chart: echarts.ECharts | null = null;
let timer: number | undefined;
let resizeObserver: ResizeObserver | undefined;

const loadAndRender = async () => {
  if (!chart) return;
  try {
    const hours = granularity.value === 'day' ? 24 * 30 : 24;
    const r = await getProxyTraffic(props.proxyId, hours, granularity.value);
    const data: TrafficBucket[] = r.data || [];
    const upSeries = data.map((b) => [b.t, b.up]);
    const downSeries = data.map((b) => [b.t, b.down]);
    chart.setOption({
      tooltip: {
        trigger: 'axis',
        formatter: (params: unknown) => {
          const items = Array.isArray(params) ? params : [params];
          if (!items.length) return '';
          const first = items[0] as { value?: [number, number] };
          const t = first.value?.[0];
          const time = t ? new Date(t).toLocaleString() : '';
          const lines = items.map((p) => {
            const item = p as { marker?: string; seriesName?: string; value?: [number, number] };
            const bytes = item.value?.[1] ?? 0;
            return `${item.marker ?? ''} ${item.seriesName ?? ''}: ${formatBytes(bytes)}`;
          });
          return [time, ...lines].filter(Boolean).join('<br/>');
        },
      },
      xAxis: { type: 'time', axisLabel: { fontSize: 10 } },
      yAxis: { type: 'value', axisLabel: { formatter: (v: number) => formatBytes(v) } },
      series: [
        { name: '上行', type: 'line', data: upSeries, smooth: true, lineStyle: { color: '#1677ff' }, areaStyle: { color: 'rgba(22,119,255,0.1)' } },
        { name: '下行', type: 'line', data: downSeries, smooth: true, lineStyle: { color: '#52c41a' }, areaStyle: { color: 'rgba(82,196,25,0.1)' } },
      ],
      grid: { top: 30, right: 20, bottom: 30, left: 60 },
    });
    // Periodic refresh: chart container might have been re-sized
    // (drawer width change, responsive breakpoint) since last render.
    chart.resize();
  } catch {
    // best-effort; don't pop error toast on a periodic refresh
  }
};

const initChart = async () => {
  if (!chartRef.value) return;
  // Defer init until the parent has its real width — without this,
  // echarts grabs the parent's pre-render width (often 0 if the
  // drawer was opened while v-if hid the chart container).
  await nextTick();
  if (!chartRef.value) return;
  // Skip if still 0-width (drawer not visible yet) — the watch below
  // will re-init once we get a real size.
  const w = chartRef.value.clientWidth;
  if (w === 0) return;
  chart = echarts.init(chartRef.value);
  // Watch for layout changes (drawer resize, viewport change, etc.)
  resizeObserver = new ResizeObserver(() => {
    chart?.resize();
  });
  resizeObserver.observe(chartRef.value);
  loadAndRender();
  timer = window.setInterval(loadAndRender, 10000);
  // Ant Design's drawer slide-in animation can leave the canvas sized
  // to the in-flight width. Force a resize after the animation settles
  // (~300ms slide + a margin).
  setTimeout(() => chart?.resize(), 350);
};

onMounted(() => {
  initChart();
});

watch(() => props.proxyId, () => {
  if (timer) {
    window.clearInterval(timer);
    timer = undefined;
  }
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = undefined;
  }
  if (chart) {
    chart.dispose();
    chart = null;
  }
  initChart();
});

onUnmounted(() => {
  if (timer) window.clearInterval(timer);
  if (resizeObserver) resizeObserver.disconnect();
  if (chart) chart.dispose();
});
</script>

<style scoped>
.traffic-chart-toolbar { display: flex; justify-content: flex-end; margin-bottom: 8px; }
</style>
